import { useAppMutation } from "../../../http/useAppMutation.ts";
import { trackEvent } from "../../../tracking/tracking.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import { TOOL_TRACKING_NAMES } from "../display.ts";
import type { GenerationType } from "../types.ts";
import { useBoardUuid } from "./useBoardUuid.ts";
import { useSelectedTool } from "./useSelectedTool.ts";
import { useUploadImage } from "./useUploadImage.ts";

export const useAddImageOnBoard = ({
  onUploaded,
  onSuccess,
  maxSize,
}: {
  onUploaded?: (asset_uuid: string) => void;
  onSuccess?: (asset_uuid: string) => void;
  maxSize: number;
}) => {
  const workspaceUuid = useWorkspaceUuid();
  const boardUuid = useBoardUuid();
  const { selectedTool } = useSelectedTool();

  const {
    mutation: { mutate: linkToBoard },
  } = useAppMutation({
    path: "boards/add-asset",
    invalidate: [
      `workspaces/${workspaceUuid}/boards`,
      `boards/${boardUuid}/assets`,
    ],
    onCacheUpdated: (response: { data: { asset_uuid: string } }) => {
      onUploaded?.(response.data.asset_uuid);
    },
    onSuccess: (response: { data: { asset_uuid: string } }) =>
      onSuccess?.(response.data.asset_uuid),
  });

  const {
    mutate: upload,
    isLoading,
    progress,
  } = useUploadImage({
    onSuccess: (contentUuid) =>
      linkToBoard({
        content_uuid: contentUuid,
        board_uuid: boardUuid,
      }),
    maxSize,
  });

  return {
    mutate: ({ image }: { image: Blob }) => {
      upload({ image });
      trackEvent(
        "content:upload",
        selectedTool
          ? {
              generation_type:
                TOOL_TRACKING_NAMES[selectedTool as GenerationType],
            }
          : {},
      );
    },
    isLoading,
    progress,
  };
};
